import React, { useEffect, useState, useRef, useCallback } from 'react'

import { Helmet } from 'react-helmet-async'
import jwtDecode from 'jwt-decode'
import * as firebaseui from 'firebaseui'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { Alert, Box, Button, Card, Divider, useTheme, Typography } from '@mui/material'
import { auth, handleFirebaseLogout } from 'providers/firebaseAuth'
import { useSearchParams } from 'react-router-dom-v5-compat'

import { get } from 'utils/lodash'
import { getPageTitle } from 'utils/seo'
import { handleError } from 'utils/error'
import { setSelectedLoginDetails } from 'slices/login/LoginPersistSlice'
import { useLazyGetBusinessInfoQuery } from 'api/business/getBusinessInfo'
import { useLazyGetAccountStatusQuery } from 'api/account/getAccountStatus'
import { useRegisterUserMutation } from 'api/account/registerUser'
import { useUpdateGCPUserMutation } from 'api/account/updateGCPUser'
import { getFirstNameAndLastName } from 'utils/parseName'
import { isSSOProvider } from 'settings/constants/auth'

import T from 'T'
import Constants from 'Constants'
import routes from 'router/routes'
import Loader from 'components/common/loader'
import BusinessLogo from 'components/common/BusinessLogo'
import { PRIMARY_BUTTON_SX, signInOptions } from './settings'
import { useMutationObserver } from './useMutationObserver'
import RecoverPassword from './RecoverPassword'
import 'firebaseui/dist/firebaseui.css'
import './style.scss'
import ContactDetails from './contact-details'
import VerifyAccount from './VerifyAccount'
import AnonymousAccess from './AnonymousAccess'
import SSOLogin from './SSOLogin'

const { PORTAL_AUTH_TENANT_ID } = Constants

const Login = () => {
  const theme = useTheme()
  const history = useHistory()
  const location = useLocation()
  const dispatch = useDispatch()
  const [searchParams, setSearchParams] = useSearchParams()
  const { pathname } = location
  const [getBusinessInfo, { isFetching: isLoadingGetBusinessInfo, data: businessInfo, isSuccess: isGetBusinessInfoSuccess }] =
    useLazyGetBusinessInfoQuery()
  const [getAccountStatus, { data: isActiveAccount, isSuccess: isGetAccountStatusSuccess }] = useLazyGetAccountStatusQuery()
  const [registerUser, { isLoading: isLoadingRegisterUser }] = useRegisterUserMutation()
  const [updateGCPUser, { isLoading: isLoadingUpdateUser }] = useUpdateGCPUserMutation()
  const [showRecoverPasswordBtn, setShowRecoverPasswordBtn] = useState(false)
  const [recoverPasswordScreen, setRecoverPasswordScreen] = useState({ isOpen: false, email: '' })
  const [hasAccountIdError, setHasAccountIdError] = useState(false)
  const [hasAccountDeactivatedError, setHasAccountDeactivatedError] = useState(false)

  // const [showCreateAccountBtn, setShowCreateAccountBtn] = useState(false)
  const [showCreateAccountForm, setShowCreateAccountForm] = useState(false)
  // const [showCreateAccountBtn, setShowCreateAccountBtn] = useState(false)
  const [showAnonymousForm, setShowAnonymousForm] = useState(false)
  const [showLoginForm, setShowLoginForm] = useState(true)

  const urlBusinessName = searchParams.get('business_name') || searchParams.get('bid')
  const oldBusinessName = useSelector(state => get(state, 'LoginPersist.selectedLoginDetails.businessName', ''), shallowEqual)
  const currentBusinessName = urlBusinessName || oldBusinessName
  const businessId = get(businessInfo, 'id', '')
  const businessTitle = get(businessInfo, 'businessTitle')
  const accountId = searchParams.get('account_id')
  const invoiceId = searchParams.get('invoice_id')
  const statementId = searchParams.get('statement_id')
  const uiRef = useRef(null)
  const mutationRef = useRef()
  const canRegister = urlBusinessName && accountId

  const resetUI = () => {}

  const goBack = (showForm = '') => {
    setShowLoginForm(showForm === 'login')
    setShowCreateAccountForm(showForm === 'create')
    setShowAnonymousForm(showForm === 'anonymous')
    // const queryParams = ''
    // history.replace({
    //   search: queryParams,
    // })
  }

  const goToCreate = () => {
    setShowLoginForm(false)
    setShowCreateAccountForm(true)
    setShowAnonymousForm(false)
    const queryParams = ''
    history.replace({
      search: queryParams,
    })
  }

  const goToHomePage = () => {
    if (invoiceId) {
      history.push(routes.app.payments)
    } else if (statementId) {
      history.push(routes.app.statements)
    } else {
      history.push(routes.app.home)
    }
  }

  const handleAccountDeactivatedError = (logout = false) => {
    setHasAccountDeactivatedError(true)
    if (logout) handleFirebaseLogout()
    resetUI()
  }

  const handleAccountIdError = (logout = false) => {
    setHasAccountIdError(true)
    if (logout) handleFirebaseLogout()
    resetUI()
  }

  const handleRegisterError = error => {
    handleError(error)
    handleFirebaseLogout()
    resetUI()
  }

  useEffect(() => {
    if (currentBusinessName) {
      getBusinessInfo(currentBusinessName)
    }
  }, [currentBusinessName])

  useEffect(() => {
    if (canRegister && businessId) {
      getAccountStatus({ businessId, accountId })
    }
  }, [canRegister, businessId])

  useEffect(() => {
    if (isGetAccountStatusSuccess) {
      // update callback to handle account status
      resetUI()
      return
    }

    if (!canRegister && isGetBusinessInfoSuccess) {
      // update callback to handle new business id
      resetUI()
    }
  }, [canRegister, isGetBusinessInfoSuccess, isGetAccountStatusSuccess])

  const handleRecoverPassword = () => {
    const emailEl = document.getElementById('ui-sign-in-email-input')
    setShowRecoverPasswordBtn(false)
    setRecoverPasswordScreen({ isOpen: true, email: get(emailEl, 'value', '') })
  }

  const mutationCallback = useCallback(() => {
    const isIDPListScreen = document.getElementsByClassName('firebaseui-idp-list')?.length
    const customAuthContainer = document.getElementById('custom-auth-container')
    if (customAuthContainer) {
      customAuthContainer.style.display = isIDPListScreen ? 'block' : 'none'
    }

    const emailEl = document.getElementById('ui-sign-in-email-input')
    if (emailEl) {
      setHasAccountIdError(false)
      setHasAccountDeactivatedError(false)
    }

    const firstAndLastNameEl = document.getElementById('ui-sign-in-name-input')
    if (firstAndLastNameEl && !canRegister) {
      handleAccountIdError()
      return
    }

    if (firstAndLastNameEl && !isActiveAccount) {
      handleAccountDeactivatedError()
      return
    }

    const trobleSignInEl = get(document.getElementsByClassName('firebaseui-form-links'), [0])
    // This observer also covers the SSOLogin subtree, which renders its own reset action on the
    // password-linking page; without this a second button opens the recover form with no email.
    const passwordLinkingPage = get(document.getElementsByClassName('firebaseui-id-page-password-linking'), [0])
    if (trobleSignInEl && !passwordLinkingPage) {
      setShowRecoverPasswordBtn(true)
      return
    }

    setShowRecoverPasswordBtn(false)
  }, [isActiveAccount])

  useMutationObserver(mutationRef, mutationCallback)

  return (
    <>
      {(isLoadingGetBusinessInfo || isLoadingRegisterUser || isLoadingUpdateUser) && <Loader />}
      {businessTitle && (
        <Helmet>
          <title>{getPageTitle(pathname, get(businessInfo, 'businessTitle'))}</title>
        </Helmet>
      )}
      <Box bgcolor="background.paper" display="flex" alignItems="center" justifyContent="center" flexDirection="column" minHeight="100vh">
        <Card sx={{ m: 'auto', width: '352px', boxShadow: `0px 1px 8px 0px ${theme.palette.divider}` }}>
          <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column" px={3}>
            <Box my={2}>{!isLoadingGetBusinessInfo && <BusinessLogo imageSX={{ height: 75 }} businessId={businessId} />}</Box>
            <Divider flexItem />
          </Box>
          <Box ref={mutationRef}>
            {!showCreateAccountForm && !showAnonymousForm && !showLoginForm ? (
              <>
                <Box
                  id="signup-container"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: 2,
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Button variant="outlined" color="primary" onClick={() => setShowLoginForm(true)} sx={{ width: '100%' }}>
                    Login Now
                  </Button>
                  <Divider sx={{ width: '100%', my: 2 }} />
                  <Button variant="outlined" color="primary" onClick={() => setShowCreateAccountForm(true)} sx={{ width: '100%' }}>
                    Create New Account
                  </Button>
                  {/* <Divider sx={{ width: '100%', my: 2 }} /> */}
                  {/* <Button */}
                  {/*  variant="outlined" */}
                  {/*  onClick={() => setShowAnonymousForm(true)} */}
                  {/*  sx={{ width: '100%' }} */}
                  {/* > */}
                  {/*  One-Time Access */}
                  {/* </Button> */}
                </Box>
              </>
            ) : showCreateAccountForm ? (
              <VerifyAccount onBack={() => goBack()} />
            ) : showAnonymousForm ? (
              <AnonymousAccess onBack={() => goBack()} />
            ) : showLoginForm ? (
              <SSOLogin onBack={() => goBack()} goToCreate={() => goToCreate()} />
            ) : null}

            {showRecoverPasswordBtn && (
              <Box position="relative">
                <Button
                  onClick={handleRecoverPassword}
                  sx={{ position: 'absolute', ml: 2, mb: 2, zIndex: theme.zIndex.tooltip, bottom: 24 }}
                  color="primary"
                  variant="text"
                >
                  Trouble signing in?
                </Button>
              </Box>
            )}

            {recoverPasswordScreen.isOpen && (
              <RecoverPassword
                isOpen={recoverPasswordScreen.isOpen}
                existingEmail={recoverPasswordScreen.email}
                businessId={businessId}
                accountId={accountId}
                invoiceId={invoiceId}
                onClose={resetUI}
              />
            )}
          </Box>
          {hasAccountIdError && (
            <Alert severity="error" sx={{ mx: 3, mb: 2, alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={400}>
                {T.ACCOUNT_NUMBER_DOESNT_EXIST}
              </Typography>
            </Alert>
          )}
          {hasAccountDeactivatedError && (
            <Alert severity="error" sx={{ mx: 3, mb: 2, alignItems: 'center' }}>
              <Typography variant="h6" fontWeight={400}>
                Cannot connect to a deactivated account. Please contact your hauler for assistance.
              </Typography>
            </Alert>
          )}
        </Card>
        <ContactDetails
          address={get(businessInfo, 'address', '')}
          email={get(businessInfo, 'businessEmail', '')}
          phone={get(businessInfo, 'phoneNumber', '')}
          website={get(businessInfo, 'website', '')}
        />
      </Box>
    </>
  )
}

export default Login
